/**
 * AnimatedPressable - Mobile
 * 
 * Pressable with smooth scale animation for better tactile feedback.
 * Use this for buttons and interactive elements.
 */

import React, { useRef } from 'react';
import { Pressable, Animated, PressableProps, ViewStyle, StyleProp } from 'react-native';

interface AnimatedPressableProps extends Omit<PressableProps, 'style'> {
  style?: StyleProp<ViewStyle>;
  pressedStyle?: StyleProp<ViewStyle>;
  scaleValue?: number;
  children: React.ReactNode;
}

export function AnimatedPressable({
  style,
  pressedStyle,
  scaleValue = 0.97,
  onPressIn,
  onPressOut,
  children,
  ...props
}: AnimatedPressableProps) {
  const scale = useRef(new Animated.Value(1)).current;

  const handlePressIn = (e: any) => {
    Animated.spring(scale, {
      toValue: scaleValue,
      useNativeDriver: true,
      speed: 50,
      bounciness: 4,
    }).start();
    onPressIn?.(e);
  };

  const handlePressOut = (e: any) => {
    Animated.spring(scale, {
      toValue: 1,
      useNativeDriver: true,
      speed: 50,
      bounciness: 4,
    }).start();
    onPressOut?.(e);
  };

  return (
    <Pressable onPressIn={handlePressIn} onPressOut={handlePressOut} {...props}>
      {({ pressed }) => (
        <Animated.View
          style={[
            style,
            pressed && pressedStyle,
            { transform: [{ scale }] },
          ]}
        >
          {children}
        </Animated.View>
      )}
    </Pressable>
  );
}

